import type { Skill } from '../skillsData';

export const agiprolabsTradingSkills: Skill[] = [
  {
    id: 'backtrader',
    name: 'backtrader',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Backtesting framework integration for strategy validation. Run historical simulations, optimize parameters, and validate trading strategies before live deployment.',
    downloads: 12500,
    stars: 890,
    version: '1.2.0',
    installCommand: '> install the backtrader skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/backtrader',
    usageExample: '> backtest strategy on BTC/USDT 2020-2024\n> optimize SMA crossover parameters\n> plot equity curve and drawdown',
    detailedDescription: 'Integration with the Backtrader Python framework for comprehensive strategy backtesting. Supports multiple data feeds, parameter optimization, walk-forward analysis, and rich visualization of equity curves, drawdowns, and trade distributions.',
  },
  {
    id: 'birdeye-api',
    name: 'birdeye-api',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Real-time Solana token data via BirdEye API. Track prices, volumes, liquidity, and wallet analytics for any SPL token.',
    downloads: 9800,
    stars: 720,
    version: '1.1.0',
    installCommand: '> install the birdeye-api skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/birdeye-api',
    usageExample: '> get BirdEye token price for BONK\n> track wallet portfolio on Solana\n> analyze token liquidity depth',
    detailedDescription: 'Full BirdEye API integration for Solana ecosystem analytics. Query real-time token prices, OHLCV data, wallet portfolios, liquidity metrics, and historical performance for any SPL token.',
  },
  {
    id: 'coingecko-api',
    name: 'coingecko-api',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'CoinGecko market data API integration. Fetch prices, market caps, volumes, historical data, and exchange tickers for 10,000+ cryptocurrencies.',
    downloads: 22100,
    stars: 1450,
    version: '2.0.0',
    installCommand: '> install the coingecko-api skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/coingecko-api',
    usageExample: '> get BTC price and market cap\n> fetch top 100 coins by volume\n> query historical OHLCV for ETH',
    detailedDescription: 'Complete CoinGecko API wrapper supporting both free and pro tiers. Access real-time and historical price data, market capitalization, trading volume, exchange tickers, coin categories, and trending searches for over 10,000 cryptocurrencies.',
    featured: true,
  },
  {
    id: 'cointegration-analysis',
    name: 'cointegration-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Statistical arbitrage pair selection via cointegration tests. Engle-Granger and Johansen tests for finding mean-reverting spread opportunities.',
    downloads: 6700,
    stars: 540,
    version: '1.0.1',
    installCommand: '> install the cointegration-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/cointegration-analysis',
    usageExample: '> test cointegration for ETH/BTC\n> run Johansen test on SOL/AVAX\n> find top 10 cointegrated pairs',
    detailedDescription: 'Statistical arbitrage toolkit for pair trading. Performs Engle-Granger two-step and Johansen multivariate cointegration tests, calculates half-life of mean reversion, and generates spread signals for market-neutral strategies.',
  },
  {
    id: 'copy-trading',
    name: 'copy-trading',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Mirror trades from successful wallets or signal providers. Track whale movements, follow smart money, and execute proportional copy trades.',
    downloads: 15400,
    stars: 1100,
    version: '1.3.0',
    installCommand: '> install the copy-trading skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/copy-trading',
    usageExample: '> copy trades from wallet 0x123...\n> mirror top performing DEX trader\n> set copy ratio to 50% of source size',
    detailedDescription: 'Social trading infrastructure for copying successful wallet addresses or signal providers. Monitors target wallets in real-time, filters for minimum size and token criteria, and executes proportional copy trades with configurable risk limits.',
    trending: true,
  },
  {
    id: 'correlation-analysis',
    name: 'correlation-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Cross-asset correlation matrices and rolling correlation analysis for portfolio construction and diversification optimization.',
    downloads: 5900,
    stars: 420,
    version: '1.0.0',
    installCommand: '> install the correlation-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/correlation-analysis',
    usageExample: '> compute 30-day correlation matrix for top 20 tokens\n> detect correlation breakdown events\n> optimize portfolio weights by risk parity',
    detailedDescription: 'Advanced correlation analytics for crypto portfolios. Computes Pearson, Spearman, and Kendall tau correlation matrices with rolling windows. Detects correlation regime changes and suggests diversification adjustments.',
  },
  {
    id: 'cost-basis-engine',
    name: 'cost-basis-engine',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Accurate cost basis calculation engine supporting FIFO, LIFO, HIFO, and specific identification methods for tax reporting.',
    downloads: 8200,
    stars: 630,
    version: '1.1.0',
    installCommand: '> install the cost-basis-engine skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/cost-basis-engine',
    usageExample: '> calculate FIFO cost basis for wallet\n> compare LIFO vs HIFO tax liability\n> export realized PnL by lot',
    detailedDescription: 'Professional-grade cost basis engine for crypto tax reporting. Supports FIFO, LIFO, HIFO, and specific identification lot methods. Handles transfers between wallets, airdrops, staking rewards, and complex DeFi interactions.',
  },
  {
    id: 'crypto-tax-export',
    name: 'crypto-tax-export',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Export transaction history to popular tax software formats. Supports TurboTax, CoinTracker, Koinly, TaxBit, and custom CSV schemas.',
    downloads: 11300,
    stars: 780,
    version: '1.2.0',
    installCommand: '> install the crypto-tax-export skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/crypto-tax-export',
    usageExample: '> export wallet history to Koinly format\n> generate TurboTax CSV for 2024\n> create custom schema for accountant',
    detailedDescription: 'Tax export pipeline for crypto transaction histories. Generates formatted exports compatible with TurboTax, CoinTracker, Koinly, TaxBit, and generic CSV. Handles DeFi, NFTs, staking, and cross-chain transactions.',
  },
  {
    id: 'custom-indicators',
    name: 'custom-indicators',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Build and deploy custom technical indicators. Define novel oscillators, trend filters, and composite signals with backtesting validation.',
    downloads: 7600,
    stars: 580,
    version: '1.0.2',
    installCommand: '> install the custom-indicators skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/custom-indicators',
    usageExample: '> build custom volatility oscillator\n> create composite trend score\n> backtest new indicator on 3 years of data',
    detailedDescription: 'Framework for creating, testing, and deploying custom technical indicators. Supports Pine Script-style syntax, statistical validation against random benchmarks, and automatic parameter optimization.',
  },
  {
    id: 'defilama-api',
    name: 'defilama-api',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'DeFi Llama TVL and protocol metrics API. Track total value locked, yields, revenue, and chain-specific DeFi analytics.',
    downloads: 13800,
    stars: 920,
    version: '1.5.0',
    installCommand: '> install the defilama-api skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/defilama-api',
    usageExample: '> get TVL for Aave on Ethereum\n> compare yields across L2 chains\n> track protocol revenue trends',
    detailedDescription: 'Comprehensive DeFi Llama API integration for total value locked, yield comparison, protocol revenue, chain analytics, stablecoin flows, and bridge volume tracking across all major DeFi protocols.',
    featured: true,
  },
  {
    id: 'dex-execution',
    name: 'dex-execution',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Smart DEX execution engine with slippage optimization, MEV protection, and multi-route splitting across aggregators.',
    downloads: 18900,
    stars: 1350,
    version: '2.1.0',
    installCommand: '> install the dex-execution skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/dex-execution',
    usageExample: '> execute swap with 0.5% max slippage\n> split large order across 3 DEXs\n> enable MEV protection via Flashbots',
    detailedDescription: 'Advanced DEX execution infrastructure with intelligent order routing, slippage estimation, MEV protection via Flashbots and MEV-Blocker, multi-route splitting, and post-trade analysis for optimal on-chain execution.',
    trending: true,
  },
  {
    id: 'dex-pool-analysis',
    name: 'dex-pool-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Deep liquidity pool analytics for Uniswap V2/V3, Curve, Balancer, and PancakeSwap. Track depth, impermanent loss, and fee APR.',
    downloads: 10200,
    stars: 740,
    version: '1.3.0',
    installCommand: '> install the dex-pool-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/dex-pool-analysis',
    usageExample: '> analyze Uniswap V3 pool depth for ETH/USDC\n> calculate IL for Curve 3pool\n> compare fee APR across DEXs',
    detailedDescription: 'Comprehensive DEX liquidity pool analytics supporting Uniswap V2/V3, Curve, Balancer, PancakeSwap, and more. Analyzes depth curves, impermanent loss scenarios, fee APR, toxic flow detection, and optimal LP ranges.',
  },
  {
    id: 'dexscreener-api',
    name: 'dexscreener-api',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Real-time DEX pair data from DexScreener. Track new pairs, volume spikes, price movements, and liquidity changes across all chains.',
    downloads: 17600,
    stars: 1280,
    version: '1.8.0',
    installCommand: '> install the dexscreener-api skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/dexscreener-api',
    usageExample: '> get trending pairs on Base\n> detect volume spike >500% in 1h\n> monitor new pair launches on Solana',
    detailedDescription: 'Full DexScreener API integration for real-time decentralized exchange analytics. Query pair profiles, price charts, volume data, liquidity metrics, and trending tokens across Ethereum, Base, Solana, BSC, Arbitrum, and more.',
    featured: true,
  },
  {
    id: 'exit-strategies',
    name: 'exit-strategies',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Systematic trade exit and position management. Trailing stops, time-based exits, profit targets, and conditional close logic.',
    downloads: 9100,
    stars: 680,
    version: '1.1.0',
    installCommand: '> install the exit-strategies skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/exit-strategies',
    usageExample: '> set trailing stop at 8% below peak\n> scale out 25% at each 10% profit level\n> time-based exit after 72 hours',
    detailedDescription: 'Comprehensive exit strategy framework for systematic position management. Supports trailing stops (ATR-based, percentage, chandelier), scale-out pyramiding, time-based exits, profit target ladders, and conditional logic combining multiple criteria.',
  },
  {
    id: 'feature-engineering',
    name: 'feature-engineering',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'ML feature engineering for crypto price prediction. Generate technical, on-chain, sentiment, and market microstructure features.',
    downloads: 6400,
    stars: 490,
    version: '1.0.1',
    installCommand: '> install the feature-engineering skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/feature-engineering',
    usageExample: '> generate rolling volatility features\n> create on-chain flow features\n> build sentiment score embeddings',
    detailedDescription: 'Machine learning feature engineering pipeline optimized for crypto prediction models. Generates technical indicator features, on-chain flow metrics, social sentiment embeddings, market microstructure signals, and automatic feature selection.',
  },
  {
    id: 'fixed-income',
    name: 'fixed-income',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'DeFi fixed income and yield analysis. Compare lending rates, bond-like positions, and structured product yields across protocols.',
    downloads: 4800,
    stars: 350,
    version: '1.0.0',
    installCommand: '> install the fixed-income skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/fixed-income',
    usageExample: '> compare USDC lending rates across Aave/Compound\n> analyze Pendle YT/PT yields\n> calculate duration and convexity for DeFi bonds',
    detailedDescription: 'Fixed income analytics for DeFi yield products. Compares lending/borrowing rates across Aave, Compound, Morpho, and others. Analyzes Pendle yield tokenization, structured product yields, and bond-equivalent metrics.',
  },
  {
    id: 'helius-api',
    name: 'helius-api',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Helius RPC and enhanced Solana APIs. Transaction parsing, NFT metadata, webhook events, and advanced account analytics.',
    downloads: 7200,
    stars: 510,
    version: '1.2.0',
    installCommand: '> install the helius-api skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/helius-api',
    usageExample: '> parse Jupiter swap transaction\n> subscribe to wallet webhook events\n> query NFT collection metadata',
    detailedDescription: 'Helius API integration for enhanced Solana development. Provides enriched RPC endpoints, transaction parsing for swaps and NFTs, webhook subscriptions, token metadata, and advanced account history queries.',
  },
  {
    id: 'impermanent-loss',
    name: 'impermanent-loss',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Impermanent loss calculator and hedging strategies for concentrated liquidity positions in Uniswap V3 and similar DEXs.',
    downloads: 8900,
    stars: 650,
    version: '1.1.0',
    installCommand: '> install the impermanent-loss skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/impermanent-loss',
    usageExample: '> calculate IL for V3 range position\n> compare IL vs fee income\n> suggest hedge ratio with perps',
    detailedDescription: 'Advanced impermanent loss modeling for concentrated liquidity AMMs. Simulates IL for custom price ranges, compares against fee income to find breakeven, and suggests perpetual futures hedging ratios.',
  },
  {
    id: 'jito-bundles',
    name: 'jito-bundles',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Jito MEV bundle submission for Solana. Bundle transactions, set tip amounts, and execute atomic multi-step strategies.',
    downloads: 6500,
    stars: 480,
    version: '1.0.2',
    installCommand: '> install the jito-bundles skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/jito-bundles',
    usageExample: '> bundle swap + stake transactions\n> set Jito tip priority fee\n> submit atomic arbitrage bundle',
    detailedDescription: 'Jito Labs MEV bundle infrastructure for Solana. Enables atomic transaction bundling with configurable tip amounts, guaranteed execution ordering, and protection against sandwich attacks through Jito\'s block engine.',
  },
  {
    id: 'kelly-criterion',
    name: 'kelly-criterion',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Kelly criterion position sizing with fractional variants. Optimize bet sizes based on edge, variance, and bankroll constraints.',
    downloads: 5400,
    stars: 410,
    version: '1.0.0',
    installCommand: '> install the kelly-criterion skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/kelly-criterion',
    usageExample: '> calculate full Kelly for strategy\n> use half-Kelly with max 5% cap\n> optimize position size by Sharpe ratio',
    detailedDescription: 'Kelly criterion implementation for optimal position sizing in trading. Supports full Kelly, fractional Kelly (half, quarter), maximum percentage caps, and dynamic adjustment based on realized Sharpe ratio and drawdown constraints.',
  },
  {
    id: 'liquidity-analysis',
    name: 'liquidity-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Market depth and liquidity analysis for order books and AMM pools. Detect shallow markets, whale walls, and manipulation.',
    downloads: 7800,
    stars: 590,
    version: '1.1.0',
    installCommand: '> install the liquidity-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/liquidity-analysis',
    usageExample: '> analyze CEX order book depth\n> detect wash trading patterns\n> estimate slippage for 100k USDT trade',
    detailedDescription: 'Comprehensive liquidity analysis toolkit. Analyzes order book depth, AMM pool curves, and on-chain flow. Detects shallow markets, spoofing, whale walls, and estimates price impact for arbitrary trade sizes.',
  },
  {
    id: 'lp-math',
    name: 'lp-math',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'AMM liquidity provision mathematics. Calculate optimal ranges, fee income projections, and rebalance triggers for V3 positions.',
    downloads: 6200,
    stars: 460,
    version: '1.0.1',
    installCommand: '> install the lp-math skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/lp-math',
    usageExample: '> calculate optimal V3 range for ETH/USDC\n> project fee income at current volume\n> set rebalance trigger at ±15% price move',
    detailedDescription: 'Mathematical toolkit for AMM liquidity providers. Computes optimal price ranges for concentrated liquidity, projects fee income based on volume and tick spacing, and suggests rebalance triggers to minimize IL and maximize yield.',
  },
  {
    id: 'market-microstructure',
    name: 'market-microstructure',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Crypto market microstructure analysis. Order flow toxicity, bid-ask bounce, trade signing, and adverse selection metrics.',
    downloads: 7100,
    stars: 520,
    version: '1.0.0',
    installCommand: '> install the market-microstructure skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/market-microstructure',
    usageExample: '> calculate VPIN for BTC futures\n> detect informed order flow\n> analyze bid-ask spread dynamics',
    detailedDescription: 'Advanced market microstructure analytics for crypto markets. Computes VPIN (volume-synchronized probability of informed trading), order flow toxicity, bid-ask bounce estimates, and adverse selection metrics across spot and derivatives markets.',
  },
  {
    id: 'market-microstructure-traditional',
    name: 'market-microstructure-traditional',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Traditional finance market microstructure models adapted for crypto. Roll model, Glosten-Milgrom, and Easley-O\'Hara metrics.',
    downloads: 4300,
    stars: 320,
    version: '1.0.0',
    installCommand: '> install the market-microstructure-traditional skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/market-microstructure-traditional',
    usageExample: '> apply Roll model to estimate spread\n> calculate Glosten-Milgrom adverse selection\n> run Easley-O\'Hara PIN decomposition',
    detailedDescription: 'Classical market microstructure theory applied to cryptocurrency markets. Implements Roll\'s covariance estimator, Glosten-Milgrom sequential trade model, and Easley-O\'Hara probability of informed trading decomposition.',
  },
  {
    id: 'mean-reversion',
    name: 'mean-reversion',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Mean reversion trading strategies for crypto. Bollinger bands, RSI extremes, z-score channels, and pairs trading signals.',
    downloads: 9500,
    stars: 710,
    version: '1.2.0',
    installCommand: '> install the mean-reversion skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/mean-reversion',
    usageExample: '> generate RSI14 oversold signals\n> trade Bollinger Band mean reversion\n> run z-score pair trade on BTC/ETH',
    detailedDescription: 'Mean reversion strategy toolkit for cryptocurrency trading. Generates signals from Bollinger bands, RSI/MFI extremes, z-score channels, Hurst exponent analysis, and Ornstein-Uhlenbeck process fitting for pairs trading.',
  },
  {
    id: 'mev-analysis',
    name: 'mev-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'MEV extraction analysis and protection. Detect sandwich attacks, frontrunning, backrunning, and arb opportunities in mempool.',
    downloads: 8700,
    stars: 640,
    version: '1.1.0',
    installCommand: '> install the mev-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/mev-analysis',
    usageExample: '> detect sandwich attack risk on trade\n> estimate MEV extraction per block\n> find backrun arbitrage opportunities',
    detailedDescription: 'Maximal Extractable Value analysis toolkit. Detects sandwich attack vulnerability, estimates frontrunning/backrunning profits, identifies arbitrage opportunities in pending transactions, and suggests MEV-protected execution routes.',
  },
  {
    id: 'ohlcv-processing',
    name: 'ohlcv-processing',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'High-performance OHLCV data processing. Resample timeframes, handle gaps, detect outliers, and normalize exchange data.',
    downloads: 10500,
    stars: 750,
    version: '1.3.0',
    installCommand: '> install the ohlcv-processing skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/ohlcv-processing',
    usageExample: '> resample 1m to 4h candles\n> detect and fill data gaps\n> normalize across 5 exchange feeds',
    detailedDescription: 'Efficient OHLCV candlestick data processing pipeline. Supports timeframe resampling, gap detection and filling, outlier removal, exchange data normalization, volume profile generation, and VWAP calculation across multiple time horizons.',
  },
  {
    id: 'options-pricing',
    name: 'options-pricing',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Crypto options pricing models. Black-Scholes, Binomial tree, Monte Carlo, and local volatility surfaces for Deribit and Lyra.',
    downloads: 6800,
    stars: 510,
    version: '1.1.0',
    installCommand: '> install the options-pricing skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/options-pricing',
    usageExample: '> price BTC call with Black-Scholes\n> build local volatility surface\n> calculate Greeks for option portfolio',
    detailedDescription: 'Comprehensive cryptocurrency options pricing library. Implements Black-Scholes, Binomial tree, and Monte Carlo simulations. Supports local and stochastic volatility surfaces, Greeks calculation, and calibration to Deribit and Lyra market data.',
  },
  {
    id: 'pandas-ta',
    name: 'pandas-ta',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Pandas Technical Analysis library integration. 130+ indicators including candles, momentum, volatility, trend, and volume.',
    downloads: 14300,
    stars: 1050,
    version: '2.0.0',
    installCommand: '> install the pandas-ta skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/pandas-ta',
    usageExample: '> calculate all 130+ indicators for BTC\n> detect candlestick patterns\n> build composite momentum score',
    detailedDescription: 'Full Pandas TA integration providing 130+ technical indicators. Covers candlestick pattern recognition, momentum oscillators, volatility measures, trend indicators, volume analysis, and composite signal construction.',
    featured: true,
  },
  {
    id: 'portfolio-analytics',
    name: 'portfolio-analytics',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Portfolio performance analytics. Sharpe, Sortino, Calmar ratios, drawdown analysis, attribution, and risk-adjusted returns.',
    downloads: 9200,
    stars: 690,
    version: '1.2.0',
    installCommand: '> install the portfolio-analytics skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/portfolio-analytics',
    usageExample: '> calculate Sharpe and Sortino ratios\n> run drawdown analysis\n> attribute returns by asset class',
    detailedDescription: 'Institutional-grade portfolio analytics for crypto. Calculates Sharpe, Sortino, Calmar, and Omega ratios. Performs drawdown analysis, return attribution by asset/strategy, beta estimation, and rolling risk metric tracking.',
  },
  {
    id: 'position-sizing',
    name: 'position-sizing',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Risk-based position sizing. Fixed fractional, volatility targeting, optimal f, and risk-parity allocation across strategies.',
    downloads: 8100,
    stars: 610,
    version: '1.1.0',
    installCommand: '> install the position-sizing skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/position-sizing',
    usageExample: '> size position at 1% risk per trade\n> target 10% annualized volatility\n> allocate by risk parity across 5 assets',
    detailedDescription: 'Advanced position sizing framework supporting fixed fractional, fixed ratio, volatility targeting, optimal f, Kelly criterion, and risk parity allocation. Integrates with stop-loss levels for integrated risk management.',
  },
  {
    id: 'pumpfun-mechanics',
    name: 'pumpfun-mechanics',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Pump.fun token launch mechanics analysis. Bonding curve math, graduation probability, sniper detection, and rug pull risk scoring.',
    downloads: 12100,
    stars: 890,
    version: '1.2.0',
    installCommand: '> install the pumpfun-mechanics skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/pumpfun-mechanics',
    usageExample: '> analyze bonding curve for new token\n> estimate graduation probability\n> detect sniper bot activity',
    detailedDescription: 'Deep analysis of Pump.fun token mechanics on Solana. Models bonding curve mathematics, estimates Raydium graduation probability, detects sniper bot patterns, and scores rug pull risk based on holder concentration and liquidity.',
    trending: true,
  },
  {
    id: 'raptor-dex',
    name: 'raptor-dex',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Raptor DEX integration for high-speed Solana trading. Jupiter routing, priority fees, and slippage-optimized execution.',
    downloads: 5500,
    stars: 400,
    version: '1.0.1',
    installCommand: '> install the raptor-dex skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/raptor-dex',
    usageExample: '> execute swap via Raptor routing\n> set dynamic priority fee\n> batch multiple DEX interactions',
    detailedDescription: 'Raptor DEX trading infrastructure for Solana. Integrates Jupiter routing for best price execution, dynamic priority fee estimation, batched transaction composition, and slippage-optimized swap execution.',
  },
  {
    id: 'regime-detection',
    name: 'regime-detection',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Market regime detection using HMM, volatility clustering, and trend strength. Adapt strategies to bull, bear, and sideways conditions.',
    downloads: 7300,
    stars: 550,
    version: '1.0.1',
    installCommand: '> install the regime-detection skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/regime-detection',
    usageExample: '> detect current market regime\n> switch strategy based on HMM state\n> backtest regime-conditioned signals',
    detailedDescription: 'Market regime identification toolkit using Hidden Markov Models, volatility clustering (GARCH), and trend strength indicators. Automatically classifies market conditions as trending up, trending down, mean-reverting, or high-volatility to adapt strategy parameters.',
  },
  {
    id: 'regulatory-reporting',
    name: 'regulatory-reporting',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Automated regulatory reporting for crypto trading. MiFID II, FATCA, and jurisdiction-specific trade and position reports.',
    downloads: 4100,
    stars: 310,
    version: '1.0.0',
    installCommand: '> install the regulatory-reporting skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/regulatory-reporting',
    usageExample: '> generate MiFID II transaction report\n> create FATCA position disclosure\n> export jurisdiction-specific summary',
    detailedDescription: 'Regulatory compliance reporting for cryptocurrency trading activities. Generates formatted reports for MiFID II transaction reporting, FATCA disclosures, and jurisdiction-specific requirements with audit trails.',
  },
  {
    id: 'risk-management',
    name: 'risk-management',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Comprehensive trading risk management. VaR, CVaR, max drawdown limits, exposure tracking, and dynamic leverage adjustment.',
    downloads: 11500,
    stars: 840,
    version: '1.4.0',
    installCommand: '> install the risk-management skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/risk-management',
    usageExample: '> calculate 95% VaR for portfolio\n> set max drawdown limit at 15%\n> adjust leverage by volatility regime',
    detailedDescription: 'Institutional risk management framework for crypto trading. Computes parametric and historical VaR/CVaR, enforces drawdown limits, tracks cross-asset exposure, and dynamically adjusts leverage based on realized volatility and correlation.',
    featured: true,
  },
  {
    id: 'sentiment-analysis',
    name: 'sentiment-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Real-time crypto sentiment from social media, news, and on-chain signals. NLP scoring, topic clustering, and contrarian indicators.',
    downloads: 10800,
    stars: 790,
    version: '1.3.0',
    installCommand: '> install the sentiment-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/sentiment-analysis',
    usageExample: '> analyze Twitter sentiment for BTC\n> detect extreme fear/greed levels\n> correlate sentiment with price moves',
    detailedDescription: 'Multi-source sentiment analysis engine for crypto markets. Processes X/Twitter, Reddit, news articles, and on-chain signals through NLP pipelines. Generates fear/greed indices, topic clusters, and contrarian extreme sentiment alerts.',
  },
  {
    id: 'shindostream',
    name: 'shindostream',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Real-time market data streaming and websocket management for multiple exchanges with automatic reconnection and failover.',
    downloads: 6200,
    stars: 450,
    version: '1.0.1',
    installCommand: '> install the shindostream skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/shindostream',
    usageExample: '> stream order book from Binance\n> aggregate trades across 3 exchanges\n> failover to backup on disconnect',
    detailedDescription: 'High-performance market data streaming infrastructure. Manages websocket connections to multiple exchanges with automatic reconnection, heartbeat monitoring, order book aggregation, trade stream normalization, and exchange failover logic.',
  },
  {
    id: 'signal-classification',
    name: 'signal-classification',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'ML-based trading signal classification. Random forest, XGBoost, and neural network models to score signal quality and filter noise.',
    downloads: 5700,
    stars: 430,
    version: '1.0.1',
    installCommand: '> install the signal-classification skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/signal-classification',
    usageExample: '> train XGBoost on historical signals\n> classify new signal as high/medium/low quality\n> filter signals below 70% confidence',
    detailedDescription: 'Machine learning pipeline for trading signal quality assessment. Trains Random Forest, XGBoost, and shallow neural networks on historical signal outcomes. Classifies new signals by predicted win rate and filters low-confidence noise.',
  },
  {
    id: 'slippage-modeling',
    name: 'slippage-modeling',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Slippage prediction models for DEX and CEX execution. Estimate price impact, optimal order splitting, and timing strategies.',
    downloads: 6900,
    stars: 520,
    version: '1.0.0',
    installCommand: '> install the slippage-modeling skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/slippage-modeling',
    usageExample: '> estimate slippage for 500k USDT swap\n> model price impact by time of day\n> optimize order splitting across venues',
    detailedDescription: 'Advanced slippage modeling for both centralized and decentralized exchanges. Estimates price impact based on order size, liquidity depth, time-of-day patterns, and volatility. Suggests optimal order splitting and execution timing.',
  },
  {
    id: 'solana-rpc',
    name: 'solana-rpc',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Solana RPC client with enhanced methods. Account queries, transaction history, program parsing, and cluster health monitoring.',
    downloads: 8400,
    stars: 620,
    version: '1.2.0',
    installCommand: '> install the solana-rpc skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/solana-rpc',
    usageExample: '> query account balance and history\n> parse Jupiter program instructions\n> monitor cluster health and TPS',
    detailedDescription: 'Comprehensive Solana RPC client with enhanced query capabilities. Supports account balance/history retrieval, program instruction parsing (Jupiter, Raydium, Pump.fun), transaction simulation, and real-time cluster health monitoring.',
  },
  {
    id: 'solana-tx-building',
    name: 'solana-tx-building',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Solana transaction builder and composer. Create complex multi-instruction transactions, set compute budgets, and simulate before send.',
    downloads: 7900,
    stars: 580,
    version: '1.1.0',
    installCommand: '> install the solana-tx-building skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/solana-tx-building',
    usageExample: '> compose swap + stake transaction\n> set compute budget and priority fee\n> simulate before sending to network',
    detailedDescription: 'Solana transaction composition toolkit for building complex multi-instruction transactions. Handles compute budget allocation, priority fee estimation, instruction ordering, transaction simulation, and partial failure recovery.',
  },
  {
    id: 'solanatracker-api',
    name: 'solanatracker-api',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'SolanaTracker API for wallet analytics, token trends, and real-time price data across the Solana ecosystem.',
    downloads: 9100,
    stars: 670,
    version: '1.3.0',
    installCommand: '> install the solanatracker-api skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/solanatracker-api',
    usageExample: '> get wallet token holdings\n> track trending Solana tokens\n> monitor price changes in real-time',
    detailedDescription: 'SolanaTracker API integration for rich Solana ecosystem analytics. Query wallet token holdings with USD values, track trending tokens, monitor real-time price changes, and analyze trading volume across Raydium, Orca, and Jupiter.',
  },
  {
    id: 'strategy-framework',
    name: 'strategy-framework',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Modular trading strategy framework. Compose entry/exit rules, risk management, and execution logic into reusable strategy modules.',
    downloads: 8600,
    stars: 640,
    version: '1.2.0',
    installCommand: '> install the strategy-framework skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/strategy-framework',
    usageExample: '> compose trend-following strategy\n> add mean reversion filter module\n> backtest composite strategy',
    detailedDescription: 'Modular strategy composition framework for systematic trading. Build strategies from reusable entry signal, exit logic, position sizing, and risk management modules. Supports multi-timeframe composites and automatic parameter optimization.',
  },
  {
    id: 'sybil-detection',
    name: 'sybil-detection',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Detect Sybil attacks and fake volume in DeFi. Cluster analysis, funding source tracing, and behavioral fingerprinting.',
    downloads: 5200,
    stars: 390,
    version: '1.0.0',
    installCommand: '> install the sybil-detection skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/sybil-detection',
    usageExample: '> detect Sybil clusters in airdrop claims\n> trace funding sources across wallets\n> fingerprint wash trading behavior',
    detailedDescription: 'Sybil attack detection toolkit for DeFi protocols and airdrops. Uses graph clustering, funding source tracing, behavioral fingerprinting, and time-pattern analysis to identify coordinated fake accounts and wash trading rings.',
  },
  {
    id: 'ta-lib',
    name: 'ta-lib',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'TA-Lib technical indicator library integration. 150+ indicators including overlays, oscillators, cycle indicators, and pattern recognition.',
    downloads: 16500,
    stars: 1200,
    version: '2.1.0',
    installCommand: '> install the ta-lib skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/ta-lib',
    usageExample: '> calculate MACD and RSI for ETH\n> detect head and shoulders pattern\n> compute ATR for volatility sizing',
    detailedDescription: 'Complete TA-Lib (Technical Analysis Library) integration providing 150+ technical indicators. Covers moving averages, momentum oscillators, volatility measures, cycle indicators, pattern recognition, and statistical functions.',
    featured: true,
  },
  {
    id: 'tax-liability-tracking',
    name: 'tax-liability-tracking',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Real-time tax liability estimation as you trade. Track realized/unrealized PnL, staking income, and airdrop tax events.',
    downloads: 7500,
    stars: 560,
    version: '1.1.0',
    installCommand: '> install the tax-liability-tracking skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/tax-liability-tracking',
    usageExample: '> estimate tax liability for current year\n> track staking income tax events\n> calculate wash sale adjustments',
    detailedDescription: 'Live tax liability tracking system for active crypto traders. Monitors realized and unrealized gains, staking/mining income, airdrop events, DeFi yield, and applies wash sale rules for accurate running tax estimates.',
  },
  {
    id: 'tax-loss-harvesting',
    name: 'tax-loss-harvesting',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'Automated tax-loss harvesting for crypto portfolios. Identify harvestable losses, avoid wash sales, and suggest replacement positions.',
    downloads: 8800,
    stars: 660,
    version: '1.2.0',
    installCommand: '> install the tax-loss-harvesting skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/tax-loss-harvesting',
    usageExample: '> scan portfolio for harvestable losses\n> suggest loss harvest trades\n> find replacement tokens to avoid wash sale',
    detailedDescription: 'Tax-loss harvesting optimizer for cryptocurrency portfolios. Identifies unrealized losses, suggests harvest trades that avoid wash sale violations, recommends similar replacement tokens, and tracks harvesting history across tax years.',
  },
  {
    id: 'token-economics',
    name: 'token-economics',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Token economics and supply dynamics analysis. Model vesting schedules, unlock events, inflation rates, and circulation impacts.',
    downloads: 6700,
    stars: 500,
    version: '1.0.1',
    installCommand: '> install the token-economics skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/token-economics',
    usageExample: '> model token vesting schedule\n> predict unlock impact on price\n> calculate real circulation vs reported',
    detailedDescription: 'Tokenomics analysis toolkit for modeling cryptocurrency supply dynamics. Projects vesting schedule unlocks, estimates price impact from token releases, calculates true circulating supply, and models inflation/deflation mechanics.',
  },
  {
    id: 'token-holder-analysis',
    name: 'token-holder-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'On-chain token holder distribution analysis. Concentration metrics, whale tracking, holder growth, and smart money identification.',
    downloads: 9400,
    stars: 700,
    version: '1.2.0',
    installCommand: '> install the token-holder-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/token-holder-analysis',
    usageExample: '> analyze holder concentration for SHIB\n> track new holder growth rate\n> identify smart money accumulation',
    detailedDescription: 'Comprehensive token holder analytics from on-chain data. Computes Gini concentration index, tracks whale wallet movements, monitors new holder acquisition rates, and identifies smart money accumulation/distribution patterns.',
  },
  {
    id: 'trade-accounting',
    name: 'trade-accounting',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Detailed trade-by-trade accounting ledger. PnL per trade, commission tracking, funding cost attribution, and strategy-level reporting.',
    downloads: 5800,
    stars: 440,
    version: '1.0.1',
    installCommand: '> install the trade-accounting skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/trade-accounting',
    usageExample: '> generate trade ledger for Q1\n> attribute PnL by strategy\n> calculate all-in cost per trade',
    detailedDescription: 'Granular trade accounting system for professional traders. Maintains detailed ledger with per-trade PnL, commission and fee tracking, funding cost attribution, and strategy-level PnL breakdowns with Sharpe and win rate by strategy.',
  },
  {
    id: 'trade-journal',
    name: 'trade-journal',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Structured trade journaling with performance analytics. Tag trades, review decisions, identify behavioral biases, and improve discipline.',
    downloads: 4900,
    stars: 370,
    version: '1.0.0',
    installCommand: '> install the trade-journal skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/trade-journal',
    usageExample: '> log trade with setup tags\n> review monthly performance by tag\n> identify FOMO entry pattern',
    detailedDescription: 'Behavioral trade journaling system for performance improvement. Log trades with setup type, emotional state, and outcome tags. Generates periodic reviews highlighting behavioral biases, best/worst setups, and discipline metrics.',
  },
  {
    id: 'trading-visualization',
    name: 'trading-visualization',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Advanced trading charts and visualizations. Candlestick patterns, volume profiles, heatmaps, correlation matrices, and equity curves.',
    downloads: 11200,
    stars: 820,
    version: '1.3.0',
    installCommand: '> install the trading-visualization skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/trading-visualization',
    usageExample: '> plot candlestick with volume profile\n> generate correlation heatmap\n> draw equity curve with underwater plot',
    detailedDescription: 'Professional trading visualization toolkit. Renders candlestick charts with volume profiles, footprint charts, correlation heatmaps, drawdown underwater plots, Monte Carlo equity projections, and strategy performance dashboards.',
  },
  {
    id: 'vectorbt',
    name: 'vectorbt',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Vectorized backtesting with VectorBT. Fast pandas/numpy backtests, parameter optimization, and portfolio simulation.',
    downloads: 7900,
    stars: 600,
    version: '1.1.0',
    installCommand: '> install the vectorbt skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/vectorbt',
    usageExample: '> run vectorized SMA crossover backtest\n> optimize parameters on Sharpe ratio\n> simulate portfolio with rebalancing',
    detailedDescription: 'VectorBT integration for lightning-fast vectorized backtesting. Leverages pandas and numpy for performance, supports parameter grid optimization, portfolio simulation with rebalancing, and rich performance analytics.',
  },
  {
    id: 'volatility-modeling',
    name: 'volatility-modeling',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Crypto volatility modeling with GARCH, EWMA, and realized volatility. Forecast variance, term structure, and volatility regimes.',
    downloads: 8600,
    stars: 650,
    version: '1.2.0',
    installCommand: '> install the volatility-modeling skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/volatility-modeling',
    usageExample: '> fit GARCH(1,1) to BTC returns\n> forecast 7-day realized volatility\n> analyze volatility term structure',
    detailedDescription: 'Advanced volatility modeling for crypto assets. Implements GARCH family models, exponentially weighted moving average (EWMA), Parkinson and Garman-Klass realized volatility estimators, and volatility term structure analysis.',
  },
  {
    id: 'walk-forward-validation',
    name: 'walk-forward-validation',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Walk-forward analysis for strategy robustness. In-sample optimization with out-of-sample validation to prevent curve fitting.',
    downloads: 5100,
    stars: 380,
    version: '1.0.0',
    installCommand: '> install the walk-forward-validation skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/walk-forward-validation',
    usageExample: '> run walk-forward on 5-year data\n> optimize in-sample, validate OOS\n> check for parameter stability',
    detailedDescription: 'Walk-forward analysis framework for robust strategy validation. Divides data into in-sample optimization and out-of-sample testing windows, rolling forward through time. Detects parameter instability and overfitting risk.',
  },
  {
    id: 'wallet-profiling',
    name: 'wallet-profiling',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'On-chain wallet behavior profiling. Identify trader types, strategy patterns, profitability, and risk profiles from transaction history.',
    downloads: 7000,
    stars: 530,
    version: '1.0.1',
    installCommand: '> install the wallet-profiling skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/wallet-profiling',
    usageExample: '> profile wallet 0x123... behavior\n> classify as DEX arbitrageur\n> estimate profitability and risk',
    detailedDescription: 'Wallet behavior analysis from on-chain transaction patterns. Classifies wallets by trading strategy (arbitrage, MEV, LP, hodler), estimates profitability, measures risk appetite, and tracks strategy evolution over time.',
  },
  {
    id: 'wash-sale-detection',
    name: 'wash-sale-detection',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Detect wash sale violations in crypto trading. Identify repurchases within 30 days and calculate disallowed loss adjustments.',
    downloads: 4600,
    stars: 340,
    version: '1.0.0',
    installCommand: '> install the wash-sale-detection skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/wash-sale-detection',
    usageExample: '> scan for wash sale violations\n> calculate disallowed loss amount\n> suggest timing to avoid rule',
    detailedDescription: 'Wash sale rule compliance detector for crypto traders. Identifies repurchases of substantially identical assets within 30 days of a loss sale, calculates disallowed loss amounts, and suggests alternative timing to maintain tax deductibility.',
  },
  {
    id: 'whale-tracking',
    name: 'whale-tracking',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Real-time whale wallet tracking and alerts. Monitor large holder movements, exchange inflows/outflows, and accumulation patterns.',
    downloads: 13400,
    stars: 980,
    version: '1.5.0',
    installCommand: '> install the whale-tracking skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/whale-tracking',
    usageExample: '> track top 100 BTC whale wallets\n> alert on exchange inflow >1000 BTC\n> detect accumulation pattern',
    detailedDescription: 'Whale monitoring and alert system for major cryptocurrencies. Tracks large holder wallet balances, detects exchange inflows/outflows that precede volatility, identifies accumulation and distribution patterns, and generates real-time alerts.',
    trending: true,
  },
  {
    id: 'yellowstone-grpc',
    name: 'yellowstone-grpc',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'tools',
    description: 'Yellowstone gRPC streaming for Solana. High-performance account, slot, and transaction subscriptions with filtering.',
    downloads: 5600,
    stars: 410,
    version: '1.0.1',
    installCommand: '> install the yellowstone-grpc skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/yellowstone-grpc',
    usageExample: '> subscribe to account updates via gRPC\n> filter transactions by program ID\n> stream slot confirmations',
    detailedDescription: 'Yellowstone gRPC streaming client for high-performance Solana data access. Supports filtered account subscriptions, program-specific transaction streaming, slot commitment tracking, and persistent connection management for low-latency applications.',
  },
  {
    id: 'yield-analysis',
    name: 'yield-analysis',
    author: 'Agipro Labs',
    authorHandle: '@agiprolabs',
    tag: 'Skill',
    category: 'defi',
    description: 'DeFi yield comparison and optimization across protocols. Track APY, IL-adjusted yields, reward token valuations, and risk scores.',
    downloads: 10100,
    stars: 730,
    version: '1.3.0',
    installCommand: '> install the yield-analysis skill from https://github.com/agiprolabs/claude-trading-skills/tree/main/skills/yield-analysis',
    usageExample: '> compare yields across Aave/Compound/Morpho\n> calculate IL-adjusted yield for LP\n> find highest risk-adjusted APY',
    detailedDescription: 'Comprehensive DeFi yield analytics platform. Compares nominal and IL-adjusted yields across lending protocols, DEX LPs, and staking. Factors in reward token valuations, protocol risk scores, and impermanent loss for true risk-adjusted returns.',
  },
];